/**
 * MY — "Change Officer" 카테고리 variable mapper.
 *
 * 현재 지원 task code:
 *   - M32105 Appointment of Director
 *
 * 변수 명세는 `preset_task/Appointment_of_Director_Variable_Map.md` 참조.
 * 변수 매칭은 case-sensitive (documentVariableReplacer.js) — snake_case 그대로 출력.
 * 문서별로 사용 변수가 다르므로, 사용하지 않는 키도 빈 문자열로 포함된다.
 */

export interface DirectorHistoryRow {
  /** Other directorship 회사명 */
  company: string;
  /** 선임/퇴임 일자 (DD Month YYYY 권장, 자유 텍스트 허용) */
  date: string;
  /** 표 행 번호 — 비우면 mapper 가 1..4 로 자동 채움 */
  no?: string | number;
}

/** Director 1명의 모든 입력 — multi-director 지원 (DCR 의 [[if multiple_directors]] / [[if directors]] 반복부). */
export interface DirectorEntry {
  fullName: string;
  nricOrPassport: string;
  passportExpiryDate?: string;
  dateOfBirth?: string;
  gender?: string;
  race?: string;
  /** B15 baseCode (예: 'B15135') — is_malaysian / is_foreigner 판별용. */
  nationalityCd?: string;
  /** 표시용 라벨 (예: 'MALAYSIA') */
  nationality?: string;
  businessOccupation?: string;
  dateOfAppointment?: string;
  residentialAddress?: string;
  serviceAddress?: string;
  email?: string;
  /** Section 58B Other Directorship — director 별로 보유 */
  history?: DirectorHistoryRow[];
}

const MALAYSIA_NATIONALITY_CODE = 'B15135';

/** Governing document type — DCR 의 [[if constitution]] / [[if articles]] / [[if third_schedule]] conditional 분기. */
export type GoverningDocumentType = 'constitution' | 'articles' | 'third_schedule';

export interface AppointmentInput {
  // ── 회사 정보 (문서 1, 2, 3, 4 공통) ──
  companyName: string;
  registrationNo: string;
  /** Constitution 조항 번호 (문서 1 DCR) */
  constitutionClauseNo?: string;
  /** DCR 의 governing document type 분기 — 'constitution' (기본) / 'articles' / 'third_schedule'. */
  governingDocumentType?: GoverningDocumentType;
  /** 회사의 기존 이사 수 — DCR [[if single_director_company]] / [[if multiple_directors_company]] 분기.
   *  (신규 임명 이사 수가 아닌 현재 회사에 등록된 이사 수 기준 — API 자동 감지.) */
  existingDirectorCount?: 'single' | 'multiple';

  // ── 신규 이사들 (1명 이상) ──
  //   DCR [[if single_director]] / [[if multiple_directors]] 조건 분기 + [[if directors]] 반복부 데이터.
  //   레거시 단일 placeholder ([[new_director_full_name]] 등) 는 첫 director 값으로 채움.
  directors: DirectorEntry[];

  // ── 날짜 ──
  resolutionDate?: string;           // 문서 1 DCR
  dateOfDeclaration?: string;        // 문서 3, 4 Section 201

  // ── 외부에서 입력된 signers (있으면 prepare-documents 가 그대로 사용) ──
  signers?: any[];
}

function trim(v: any): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** Malaysia NRIC 표준 포맷: 12자리 숫자면 XXXXXX-XX-XXXX 로 표시. */
function formatMalaysiaNricForDoc(raw: any): string {
  const s = trim(raw);
  if (!s) return '';
  // 숫자만 추출 (공백/하이픈/괄호 등 제거)
  const digits = s.replace(/\D/g, '');
  if (digits.length === 12) {
    return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
  }
  // 12자리 아니면 원문 유지(여권 등)
  return s;
}

/**
 * Other directorship 반복부.
 *
 * 템플릿 (Section 58B):
 *   [[if direct_his]]
 *     [[new_director_history_no]] | [[new_director_history_company]] | [[new_director_history_date]]
 *   [[/if direct_his]]
 *
 * → loop key = `direct_his`, inner var keys 는 셀에 표기된 그대로 (`new_director_history_no/company/date`).
 * AGM `retiring_directors` 와 동일 패턴.
 *
 * 비어있는 행(회사명+날짜 둘 다 공란)은 자동 제외.
 */
function buildHistoryRows(rows: DirectorHistoryRow[] | undefined): {
  loop: Array<{
    new_director_history_no: string;
    new_director_history_company: string;
    new_director_history_date: string;
  }>;
} {
  const list = Array.isArray(rows) ? rows : [];
  const loop = list
    .map((r, i) => ({
      new_director_history_no: trim(r.no) || String(i + 1),
      new_director_history_company: trim(r.company),
      new_director_history_date: trim(r.date),
    }))
    .filter((r) => r.new_director_history_company || r.new_director_history_date);
  // 행 번호 재정렬 (필터 후)
  loop.forEach((r, i) => {
    r.new_director_history_no = String(i + 1);
  });
  return { loop };
}

/** Director 한 명을 mapper row 로 — 반복부 [[if directors]] ... [[endif]] 안에서 사용되는 키. */
function buildDirectorRow(d: DirectorEntry | null | undefined): Record<string, any> {
  if (!d) {
    return {
      new_director_full_name: '',
      new_director_nric_or_passport_number: '',
      new_director_passport_expiry_date: '',
      new_director_date_of_birth: '',
      new_director_gender: '',
      new_director_race: '',
      new_director_nationality: '',
      new_director_business_occupation: '',
      new_director_date_of_appointment: '',
      new_director_residential_address: '',
      new_director_service_address: '',
      new_director_email_address: '',
      is_malaysian: false,
      is_foreigner: false,
      is_passport: '',
    };
  }
  const natCd = trim(d.nationalityCd).toUpperCase();
  const isMalaysian =
    natCd === MALAYSIA_NATIONALITY_CODE ||
    (!natCd && /malaysia/i.test(trim(d.nationality)));
  const isForeigner = !!(natCd || trim(d.nationality)) && !isMalaysian;
  const hasPassportExpiry = !!trim(d.passportExpiryDate);
  return {
    new_director_full_name: trim(d.fullName),
    // MY NRIC 는 문서에서 하이픈 제거하지 않고 표준 포맷(XXXXXX-XX-XXXX) 적용.
    new_director_nric_or_passport_number: isMalaysian
      ? formatMalaysiaNricForDoc(d.nricOrPassport)
      : trim(d.nricOrPassport),
    new_director_passport_expiry_date: trim(d.passportExpiryDate),
    new_director_date_of_birth: trim(d.dateOfBirth),
    new_director_gender: trim(d.gender),
    new_director_race: trim(d.race),
    new_director_nationality: trim(d.nationality),
    new_director_business_occupation: trim(d.businessOccupation),
    new_director_date_of_appointment: trim(d.dateOfAppointment),
    new_director_residential_address: trim(d.residentialAddress),
    new_director_service_address: trim(d.serviceAddress),
    new_director_email_address: trim(d.email),
    is_malaysian: isMalaysian,
    is_foreigner: isForeigner,
    is_passport: (isForeigner || hasPassportExpiry) ? '1' : '',
  };
}

export function buildAppointmentVariables(input: AppointmentInput): Record<string, any> {
  const companyName = trim(input.companyName);
  const companyRegistrationNo = trim(input.registrationNo);
  const constitutionClauseNumber = trim(input.constitutionClauseNo);
  const resolutionDate = trim(input.resolutionDate);
  const dateOfDeclaration = trim(input.dateOfDeclaration);

  // Director 배열 정규화 — 최소 1명 (빈 배열이면 빈 director 1개).
  const rawDirectors = Array.isArray(input.directors) && input.directors.length > 0
    ? input.directors
    : [{ fullName: '', nricOrPassport: '' } as DirectorEntry];

  // 반복부 row + 첫 director (legacy 단일 placeholder 호환).
  const directorsLoop = rawDirectors.map(buildDirectorRow);
  const primary = directorsLoop[0];

  // 1명/N명 분기 boolean.
  const singleDirector = rawDirectors.length === 1;
  const multipleDirectors = rawDirectors.length > 1;

  // Governing document type 3-way 분기 — radio 1택. 기본값: constitution.
  const govType: GoverningDocumentType = input.governingDocumentType || 'constitution';

  // RESOLVED 문구의 "in terms of …" 결의 근거 텍스트 — 3종 모두 올바른 완성 문구.
  //   Third Schedule 은 조항 번호가 없으므로 [[constitution_clause_number]] 단독 토큰은 비어버린다.
  //   템플릿은 조건 블록 대신 단일 토큰 [[resolution_basis_clause]] 를 쓰면 3종 모두 안전하게 채워진다.
  const resolutionBasisClause =
    govType === 'third_schedule'
      ? 'the Third Schedule of the Companies Act 2016'
      : govType === 'articles'
        ? (constitutionClauseNumber
            ? `Article ${constitutionClauseNumber} of the Articles of Association`
            : 'the Articles of Association')
        : (constitutionClauseNumber
            ? `Clause ${constitutionClauseNumber} of the Constitution`
            : 'the Constitution');

  // Other Directorship 반복부 — 첫 director 의 history 만 적용 (Section 58B 는 1명짜리 폼).
  //   향후 per-director Section 58B 가 필요하면 prepare-documents 단계에서 분리 생성.
  const history = buildHistoryRows(rawDirectors[0]?.history);

  const out: Record<string, any> = {
    // ── 회사 정보 ──
    company_name: companyName,
    company_registration_no: companyRegistrationNo,
    registration_number: companyRegistrationNo,      // Section 58B 템플릿 alias
    constitution_clause_number: constitutionClauseNumber,
    // 결의 근거 완성 문구 — 템플릿에서 [[resolution_basis_clause]] 단일 토큰으로 사용 (3종 모두 채워짐, Third Schedule 포함).
    resolution_basis_clause: resolutionBasisClause,
    governing_document_clause: resolutionBasisClause,

    // ── 신규 이사 단일 placeholder (첫 director — 레거시 / 1명짜리 문서 호환) ──
    ...primary,

    // ── 신규 이사 반복부 — [[if directors]] [[new_director_full_name]] ... [[endif]] 같은 N-row 출력 ──
    directors: directorsLoop,

    // ── Conditional boolean — DCR / Section 58B 의 [[if X]] ... [[endif]] 분기 ──
    constitution: govType === 'constitution',
    articles: govType === 'articles',
    third_schedule: govType === 'third_schedule',
    single_director: singleDirector,
    multiple_directors: multipleDirectors,
    // 새 AOD DCR 템플릿 토큰 alias (appoint_* / resolution_basis_*).
    appoint_single_director: singleDirector,
    appoint_multiple_directors: multipleDirectors,
    resolution_basis_constitution: govType === 'constitution',
    resolution_basis_articles: govType === 'articles',
    resolution_basis_third_schedule: govType === 'third_schedule',
    // 회사 기존 이사 수 기준 — DCR [[if single_director_company]] / [[if multiple_directors_company]].
    // existingDirectorCount 미제공 시 신규 임명 이사 수로 fallback.
    single_director_company: input.existingDirectorCount
      ? input.existingDirectorCount === 'single'
      : singleDirector,
    multiple_directors_company: input.existingDirectorCount
      ? input.existingDirectorCount === 'multiple'
      : multipleDirectors,
    // 새 DCR 템플릿 토큰 alias — [[if is_sole_director_company]] / [[if is_multiple_director_company]].
    is_sole_director_company: input.existingDirectorCount
      ? input.existingDirectorCount === 'single'
      : singleDirector,
    is_multiple_director_company: input.existingDirectorCount
      ? input.existingDirectorCount === 'multiple'
      : multipleDirectors,
    // 최상위 레벨 — 단일 director 문서 (Section 58B, Section 201) 에서 직접 [[if is_malaysian]] 사용 가능.
    is_malaysian: primary.is_malaysian,
    is_foreigner: primary.is_foreigner,

    // ── Other Directorship 반복부 ([[if direct_his]] ... [[/if direct_his]]) ──
    direct_his: history.loop,

    // ── 날짜 ──
    resolution_date: resolutionDate,
    date_of_declaration: dateOfDeclaration,

    // ── UPPER_CASE alias (레거시 템플릿 호환) ──
    COMPANY_NAME: companyName,
    COMPANY_REGISTRATION_NO: companyRegistrationNo,
    NEW_DIRECTOR_FULL_NAME: primary.new_director_full_name,
  };

  // MY 박스형 레이아웃 — per-signer 플래그 + top-level 변수 둘 다 저장.
  // top-level: 기존 태스크를 T-Doc AI에서 열 때 백엔드가 layout 감지 가능.
  out._signerLayout = 'box';
  if (Array.isArray(input.signers) && input.signers.length > 0) {
    out.signers = input.signers.map((s: any) => ({ ...s, _signerLayout: 'box' }));
  }

  return out;
}

/**
 * docCode / templateName 기반으로 Change Officer 문서 종류를 분류.
 * 한 PreSet 에 포함된 문서 목록을 보고 어떤 입력 섹션을 활성화할지 결정.
 *
 * Appointment of Director 의 4개 문서 매핑:
 *   1) Directors' Circular Resolution         → hasDcr
 *   2) Section 58B (New Director)             → hasSection58B
 *   3) Section 201 with attestation           → hasSection201Attest
 *   4) Section 201 (plain, no attestation)    → hasSection201Plain
 */
export interface ChangeOfficerDocFlags {
  hasDcr: boolean;
  hasSection58B: boolean;
  hasSection201Attest: boolean;
  hasSection201Plain: boolean;
  hasSection201Either: boolean;
  /** 신규 이사 폼이 필요한가 (어떤 문서든 hit 되면 true) */
  needsNewDirectorForm: boolean;
}

/**
 * Secretary 자격 유형 — 자격단체(CA2016 4th Schedule + S.20G) 접두사.
 *   `[[new_secretary_*_license_or_membership_number]]` (Section 58H, 236(3)) 는
 *   "MAICSA 12345" / "MIA 5678" / "LS 90" 형식으로 조합.
 *   DCR 은 분리된 변수 (maicsa_number / mia_number / ls_number) 받음 — 선택한 유형의 번호만 채우고 나머지는 빈값.
 *   ※ BC(Bar Council)는 'BC/S/1235' 슬래시형이라 접두사+숫자 모델 제외(전체 라이선스 직접 입력).
 */
export type SecretaryQualificationType = 'MAICSA' | 'MIA' | 'MACS' | 'MICPA' | 'LS' | 'SLS' | 'SAA';

export interface SecretaryEntry {
  fullName: string;
  /** clearable UI 에서 null 도 set 되므로 허용. mapper 는 trim() 으로 정규화. */
  qualificationType: SecretaryQualificationType | '' | null;
  qualificationNumber: string;
  ssmPractisingCertificateNumber?: string;
  /** Section 58H / 236(3) 용 추가 필드 (비서 1만 — 58H 는 1명, 236(3) 도 1명씩). */
  nricOrPassport?: string;
  dateOfBirth?: string;
  gender?: string;
  race?: string;
  /** B15 baseCode (예: 'B15135') — payload nationality 필드용. */
  nationalityCd?: string;
  /** 표시용 라벨 (예: 'MALAYSIA') — UPPER_CASE alias 와 chip 표시용. */
  nationality?: string;
  businessOccupation?: string;
  dateOfAppointment?: string;
  residentialAddress?: string;
  /** Section 236(3) 전용 필드 — 비서 사무실/연락처 등. */
  firm?: string;
  tel?: string;
  email?: string;
  fax?: string;
}

export interface SecretaryAppointmentInput {
  // 회사 정보 (3개 문서 공통)
  companyName: string;
  registrationNo: string;
  constitutionClauseNumber?: string;

  // DCR 의 [[if constitution]] / [[if articles]] / [[if third_schedule]] 분기. 기본값: constitution.
  governingDocumentType?: GoverningDocumentType;
  // DCR 의 [[if single_director_company]] / [[if multiple_directors_company]] 분기.
  directorCount?: 'single' | 'multiple';

  // 비서 1 (필수) + 2 (선택)
  secretary1: SecretaryEntry;
  secretary2?: SecretaryEntry | null;

  // 날짜
  resolutionDate?: string;          // DCR
  dateOfDeclaration?: string;       // Section 236(3)

  // 외부 signers (PreSet 단계에선 비워두고 사용자 후속 선택)
  signers?: any[];
}

export interface SecretaryAppointmentDocFlags {
  hasDcr: boolean;          // Directors' Circular Resolution (Appointment of Secretaries)
  hasSection58H: boolean;   // SSM 제출용 양식
  hasSection236_3: boolean; // 비서 본인 선언서
  /** 신규 비서 폼이 필요한가 (어떤 문서든 hit 되면 true) */
  needsNewSecretaryForm: boolean;
}

/** 자격 유형 + 번호 → 통합 라벨 (Section 58H / 236(3)) — "MAICSA 12345" 형식. 빈값이면 빈문자열. */
function composeLicenseLabel(type: SecretaryQualificationType | '' | undefined, num: string): string {
  const t = trim(type);
  const n = trim(num);
  if (!t || !n) return n;
  return `${t} ${n}`.trim();
}

/** Secretary 한 명에 대해 mapper 변수 set 구성 — 인덱스 prefix (`new_secretary_1_` / `new_secretary_2_`) 적용. */
function buildSecretaryFieldSet(prefix: string, sec: SecretaryEntry | null | undefined): Record<string, any> {
  if (!sec) {
    // DCR 의 [[if new_secretary_2_full_name]] 분기 — full_name 이 빈값이면 블록이 빠짐.
    return {
      [`${prefix}_full_name`]: '',
      [`${prefix}_maicsa_number`]: '',
      [`${prefix}_mia_number`]: '',
      [`${prefix}_ls_number`]: '',
      [`${prefix}_ssm_practising_certificate_number`]: '',
      [`${prefix}_license_or_membership_number`]: '',
      [`${prefix}_nric_or_passport_number`]: '',
      [`${prefix}_date_of_birth`]: '',
      [`${prefix}_gender`]: '',
      [`${prefix}_race`]: '',
      [`${prefix}_nationality`]: '',
      [`${prefix}_business_occupation`]: '',
      [`${prefix}_date_of_appointment`]: '',
      [`${prefix}_residential_address`]: '',
      [`${prefix}_firm`]: '',
      [`${prefix}_tel`]: '',
      [`${prefix}_email`]: '',
      [`${prefix}_fax`]: '',
    };
  }
  const type = trim(sec.qualificationType);
  const number = trim(sec.qualificationNumber);
  const natCd = trim((sec as any)?.nationalityCd).toUpperCase();
  const isMalaysian =
    natCd === MALAYSIA_NATIONALITY_CODE ||
    (!natCd && /malaysia/i.test(trim((sec as any)?.nationality)));
  return {
    [`${prefix}_full_name`]: trim(sec.fullName),
    // DCR 은 세 변수에 슬래시 나열 — 해당 유형에만 번호 채우고 나머지는 빈값.
    [`${prefix}_maicsa_number`]: type === 'MAICSA' ? number : '',
    [`${prefix}_mia_number`]: type === 'MIA' ? number : '',
    [`${prefix}_ls_number`]: type === 'LS' ? number : '',
    [`${prefix}_ssm_practising_certificate_number`]: trim(sec.ssmPractisingCertificateNumber),
    // Section 58H / 236(3) 통합 라벨: "MAICSA 12345"
    [`${prefix}_license_or_membership_number`]: composeLicenseLabel(type as SecretaryQualificationType, number),
    [`${prefix}_nric_or_passport_number`]: isMalaysian
      ? formatMalaysiaNricForDoc(sec.nricOrPassport)
      : trim(sec.nricOrPassport),
    [`${prefix}_date_of_birth`]: trim(sec.dateOfBirth),
    [`${prefix}_gender`]: trim(sec.gender),
    [`${prefix}_race`]: trim(sec.race),
    [`${prefix}_nationality`]: trim(sec.nationality),
    [`${prefix}_business_occupation`]: trim(sec.businessOccupation),
    [`${prefix}_date_of_appointment`]: trim(sec.dateOfAppointment),
    [`${prefix}_residential_address`]: trim(sec.residentialAddress),
    [`${prefix}_firm`]: trim(sec.firm),
    [`${prefix}_tel`]: trim(sec.tel),
    [`${prefix}_email`]: trim(sec.email),
    [`${prefix}_fax`]: trim(sec.fax),
  };
}

/**
 * DCR 반복부 (`[[if secretary]] ... [[/if secretary]]`) 의 row 빌더.
 *   loop key = `secretary`, inner var keys = 셀에 적힌 그대로:
 *     [[new_secretary_full_name]]
 *     [[new_secretary_license_type]]    — MAICSA / MIA / LS
 *     [[new_secretary_license_number]]
 *     [[new_secretary_ssm_practising_certificate_number]]
 *   fullName 빈 행은 자동 제외.
 */
function buildSecretaryLoopRows(
  secretaries: Array<SecretaryEntry | null | undefined>
): Array<Record<string, string>> {
  return (secretaries || [])
    .filter((s): s is SecretaryEntry => !!(s && String(s.fullName || '').trim()))
    .map((s) => ({
      new_secretary_full_name: trim(s.fullName),
      new_secretary_license_type: trim(s.qualificationType),
      new_secretary_license_number: trim(s.qualificationNumber),
      new_secretary_ssm_practising_certificate_number: trim(s.ssmPractisingCertificateNumber),
    }));
}

export function buildSecretaryAppointmentVariables(
  input: SecretaryAppointmentInput
): Record<string, any> {
  const companyName = trim(input.companyName);
  const companyRegistrationNo = trim(input.registrationNo);
  const constitutionClauseNumber = trim(input.constitutionClauseNumber);
  const resolutionDate = trim(input.resolutionDate);
  const dateOfDeclaration = trim(input.dateOfDeclaration);

  // Section 58H / 236(3) 호환용 _1_ / _2_ prefix 변수 (1명짜리 문서 호환 유지).
  const sec1 = buildSecretaryFieldSet('new_secretary_1', input.secretary1);
  const sec2 = buildSecretaryFieldSet('new_secretary_2', input.secretary2);
  // DCR 새 반복부 — 단일 loop key 'secretary' 에 모든 비서 row.
  const secretaryLoop = buildSecretaryLoopRows([input.secretary1, input.secretary2]);

  // Governing document type + director count — DCR 분기 변수.
  const govType: GoverningDocumentType = input.governingDocumentType || 'constitution';
  const isSingle = input.directorCount === 'single';
  // 결의 근거 완성 문구 (Third Schedule 포함 3종) — 템플릿 [[resolution_basis_clause]] 단일 토큰용.
  const resolutionBasisClause =
    govType === 'third_schedule'
      ? 'the Third Schedule of the Companies Act 2016'
      : govType === 'articles'
        ? (constitutionClauseNumber
            ? `Article ${constitutionClauseNumber} of the Articles of Association`
            : 'the Articles of Association')
        : (constitutionClauseNumber
            ? `Clause ${constitutionClauseNumber} of the Constitution`
            : 'the Constitution');

  const out: Record<string, any> = {
    // 회사 정보
    company_name: companyName,
    company_registration_no: companyRegistrationNo,
    registration_number: companyRegistrationNo,     // Section 236(3) / Section 58H 템플릿 alias
    registration_no: companyRegistrationNo,
    constitution_clause_number: constitutionClauseNumber,
    // 결의 근거 완성 문구 — 템플릿 [[resolution_basis_clause]] 단일 토큰 (3종 모두, Third Schedule 포함).
    resolution_basis_clause: resolutionBasisClause,
    governing_document_clause: resolutionBasisClause,

    // DCR [[if constitution]] / [[if articles]] / [[if third_schedule]] 분기
    constitution: govType === 'constitution',
    articles: govType === 'articles',
    third_schedule: govType === 'third_schedule',

    // DCR [[if single_director_company]] / [[if multiple_directors_company]] 분기
    single_director_company: isSingle,
    multiple_directors_company: !isSingle,
    // 새 DCR 토큰 alias — [[if is_sole_director_company]] / [[if is_multiple_director_company]] + resolution_basis_*
    is_sole_director_company: isSingle,
    is_multiple_director_company: !isSingle,
    resolution_basis_constitution: govType === 'constitution',
    resolution_basis_articles: govType === 'articles',
    resolution_basis_third_schedule: govType === 'third_schedule',
    // 비서 단/복수 — [[if appoint_single_secretary]] / [[if appoint_multiple_secretaries]]
    //   (mapper 는 secretary1/2 만 봄 → 패널이 N명일 때 override). loop 길이 기준.
    appoint_single_secretary: secretaryLoop.length <= 1,
    appoint_multiple_secretaries: secretaryLoop.length > 1,

    // 비서 1 + 2 prefix 필드 (Section 58H / 236(3))
    ...sec1,
    ...sec2,

    // DCR 반복부 — [[if secretary]] (구) / [[if secretaries]] (신) 둘 다 지원.
    secretary: secretaryLoop,
    secretaries: secretaryLoop,

    // 날짜
    resolution_date: resolutionDate,
    date_of_declaration: dateOfDeclaration,

    // UPPER_CASE alias (레거시 템플릿 호환)
    COMPANY_NAME: companyName,
    COMPANY_REGISTRATION_NO: companyRegistrationNo,
    CONSTITUTION_CLAUSE_NUMBER: constitutionClauseNumber,
    NEW_SECRETARY_1_FULL_NAME: sec1.new_secretary_1_full_name,
    NEW_SECRETARY_2_FULL_NAME: sec2.new_secretary_2_full_name,
  };

  if (Array.isArray(input.signers) && input.signers.length > 0) {
    out.signers = input.signers;
  }

  return out;
}

/**
 * docCode / templateName 으로 Appointment of Secretary 문서 종류를 분류.
 *   1) Directors' Circular Resolution (Appointment of Secretaries)  → hasDcr
 *   2) Section 58H (New Secretary)                                  → hasSection58H
 *   3) Section 236(3)                                               → hasSection236_3
 */
export function detectSecretaryAppointmentDocFlags(
  documents: Array<{ templateName?: string; docCode?: string }>
): SecretaryAppointmentDocFlags {
  const names = (documents || []).map((d) => (d.templateName || '').toLowerCase());
  const hasDcr = names.some(
    (n) =>
      n.includes('circular resolution') ||
      (n.includes('directors') && n.includes('resolution'))
  );
  const hasSection58H = names.some((n) => /section\s*58\s*h/.test(n));
  const hasSection236_3 = names.some(
    (n) => /section\s*236/.test(n) && /3/.test(n) // "Section 236(3)" / "Section 236 3" 둘 다 매치
  );
  return {
    hasDcr,
    hasSection58H,
    hasSection236_3,
    needsNewSecretaryForm: hasDcr || hasSection58H || hasSection236_3,
  };
}

export function detectChangeOfficerDocFlags(
  documents: Array<{ templateName?: string; docCode?: string }>
): ChangeOfficerDocFlags {
  const names = (documents || []).map((d) => (d.templateName || '').toLowerCase());
  const hasDcr = names.some(
    (n) =>
      n.includes('circular resolution') ||
      (n.includes('directors') && n.includes('resolution'))
  );
  const hasSection58B = names.some((n) => /section\s*58\s*b/.test(n));
  // Section 201: "with attestation" / "plain" / 또는 "no attestation"
  const hasSection201Attest = names.some(
    (n) => /section\s*201/.test(n) && /attest/.test(n) && !/no\s*attest/.test(n)
  );
  const hasSection201Plain = names.some(
    (n) =>
      /section\s*201/.test(n) &&
      (/plain/.test(n) || /no\s*attest/.test(n) || !/attest/.test(n))
  );
  const hasSection201Either = hasSection201Attest || hasSection201Plain;

  return {
    hasDcr,
    hasSection58B,
    hasSection201Attest,
    hasSection201Plain,
    hasSection201Either,
    needsNewDirectorForm: hasDcr || hasSection58B || hasSection201Either,
  };
}
